import * as styles from "./TimePickerContent.css";

import { clsx } from "clsx";
import type { JSX } from "react";
import { useId, useMemo } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { pointerAlignOffset } from "@frontend/utils/pointerAlign";
import { useLingui } from "@lingui/react/macro";

import { useTimePickerContext } from "./TimePicker.context";
import type {
  TimePickerContentProps,
  TimePickerEndpoint,
} from "./TimePicker.types";
import { TimePickerArrow } from "./TimePickerArrow";
import { TimePickerBackdrop } from "./TimePickerBackdrop";
import { TimePickerColumn } from "./TimePickerColumn";
import { TimePickerPortal } from "./TimePickerPortal";
import { ARROW_SIZES } from "./constants";

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const GAP = 4;

export function TimePickerContent({
  className,
  side = "bottom",
  sideOffset,
  align = "center",
  alignOffset,
  positionMethod,
  onKeyDown,
  ...props
}: TimePickerContentProps): JSX.Element {
  const { t } = useLingui();

  const {
    range,
    selectedRange,
    minuteStep,
    selectHour,
    selectMinute,
    pointerOffsetRef,
    arrow,
    size,
    typeKey,
    typing,
  } = useTimePickerContext();

  const labelId = useId();

  const minutes = useMemo(
    () =>
      Array.from(
        { length: Math.ceil(60 / minuteStep) },
        (_, index) => index * minuteStep,
      ),
    [minuteStep],
  );

  const [start, end] = selectedRange;

  function columns(endpoint: TimePickerEndpoint) {
    const isEnd = endpoint === "end";
    const current = isEnd ? (end ?? start) : start;
    const floor = isEnd ? start : null;

    const hourItems = floor
      ? HOURS.filter((hour) => hour >= floor.hour)
      : HOURS;

    const minuteItems =
      floor && (current?.hour ?? floor.hour) === floor.hour
        ? minutes.filter((minute) => minute >= floor.minute)
        : minutes;

    return (
      <>
        <TimePickerColumn
          items={hourItems}
          selected={current?.hour ?? 0}
          onSelect={(hour) => selectHour(hour, endpoint)}
          aria-label={t({ message: "Hours", context: "time picker" })}
        />
        <span className={styles.separator}>:</span>
        <TimePickerColumn
          items={minuteItems}
          selected={current?.minute ?? 0}
          onSelect={(minute) => selectMinute(minute, endpoint)}
          aria-label={t({ message: "Minutes", context: "time picker" })}
        />
      </>
    );
  }

  function group(endpoint: TimePickerEndpoint, label: string) {
    const id = `${labelId}-${endpoint}`;

    return (
      <div className={styles.group}>
        <span
          id={id}
          data-typing={typing?.endpoint === endpoint ? "" : undefined}
          className={styles.groupLabel}
        >
          {label}
        </span>
        <div role="group" aria-labelledby={id} className={styles.columns}>
          {columns(endpoint)}
        </div>
      </div>
    );
  }

  return (
    <TimePickerPortal>
      <TimePickerBackdrop />
      <BasePopover.Positioner
        data-slot="time-picker-positioner"
        side={side}
        sideOffset={sideOffset ?? (arrow ? ARROW_SIZES[size] : GAP)}
        align={align}
        alignOffset={alignOffset ?? pointerAlignOffset(pointerOffsetRef, arrow)}
        positionMethod={positionMethod}
        className={styles.positioner}
      >
        <BasePopover.Popup
          data-slot="time-picker-popup"
          data-range={range ? "" : undefined}
          className={clsx(styles.popup, className)}
          onKeyDown={(event) => {
            if (typeKey(event.key)) {
              event.preventDefault();
            }

            onKeyDown?.(event);
          }}
          {...props}
        >
          {arrow && <TimePickerArrow />}
          {range ? (
            <>
              {group("start", t({ message: "Start", context: "time picker" }))}
              <span className={styles.divider} />
              {group("end", t({ message: "End", context: "time picker" }))}
            </>
          ) : (
            columns("start")
          )}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </TimePickerPortal>
  );
}
